import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { apiJson } from "../lib/queryClient";

type Entitlement = {
  active: boolean;
  source: "admin" | "manual" | "stripe" | "trial" | "none";
  plan: {
    productId: string | null;
    productName: string | null;
    priceId: string | null;
    interval: string | null;
    amount: number | null;
  } | null;
  status: string | null;
  currentPeriodEnd: string | null;
};

type AdminUserDetail = {
  user: {
    id: string;
    email: string;
    name: string | null;
    isAdmin: boolean;
    authProvider: string;
    createdAt: string;
    updatedAt: string;
  };
  subscription: Entitlement | null;
  manualPlan: {
    productId: string | null;
    unlimited: boolean | null;
    until: string | null;
    note: string | null;
  };
  stripeCustomerId: string | null;
  trialStartedAt: string | null;
  profile: Record<string, unknown> | null;
  settings: Record<string, unknown> | null;
  drones: Array<Record<string, unknown>>;
  sessions: Array<Record<string, unknown>>;
  logbook: Array<Record<string, unknown>>;
  glossary: Array<Record<string, unknown>>;
  resources: Array<Record<string, unknown>>;
  droneHistory: Array<Record<string, unknown>>;
};

function Section({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  return (
    <section className="bg-white border border-[var(--color-app-border)] rounded-lg overflow-hidden">
      <header className="px-4 py-2 bg-[var(--color-app-bg-secondary)] border-b border-[var(--color-app-border)] flex items-center justify-between">
        <h2 className="font-semibold text-[var(--color-app-text)]">{title}</h2>
        <span className="text-xs text-[var(--color-app-text-secondary)]">{count} Einträge</span>
      </header>
      <div className="p-3 max-h-96 overflow-auto">{children}</div>
    </section>
  );
}

function JsonList({ items }: { items: Array<Record<string, unknown>> }) {
  if (items.length === 0)
    return <div className="text-[var(--color-app-text-tertiary)] text-sm italic">Keine Einträge.</div>;
  return (
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={(it.id as string) || i} className="text-xs">
          <pre className="bg-[var(--color-app-bg-secondary)] border border-[var(--color-app-border)] rounded p-2 overflow-x-auto">
            {JSON.stringify(it, null, 2)}
          </pre>
        </li>
      ))}
    </ul>
  );
}

function PasswordModal({
  email,
  password,
  onClose,
}: {
  email: string;
  password: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4 shadow-xl">
        <h2 className="text-lg font-semibold text-[var(--color-app-text)]">Neues Passwort für {email}</h2>
        <p className="text-sm text-[var(--color-app-text-secondary)]">
          Das Passwort wird nur einmalig angezeigt. Bitte sicher an den Nutzer übergeben.
        </p>
        <div className="flex gap-2 items-center">
          <code className="flex-1 px-3 py-2 bg-[var(--color-app-bg-tertiary)] border border-[var(--color-app-border)] rounded font-mono text-sm break-all">
            {password}
          </code>
          <button
            onClick={copy}
            className="px-3 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm rounded"
          >
            {copied ? "Kopiert" : "Kopieren"}
          </button>
        </div>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-[var(--color-app-bg-tertiary)] hover:bg-[var(--color-app-bg-tertiary)] rounded"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminUserDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<AdminUserDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Editable fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const [pwModal, setPwModal] = useState<{ email: string; password: string } | null>(null);

  // Manual plan (free/override) state
  const [packages, setPackages] = useState<{ id: string; name: string }[]>([]);
  const [mpProductId, setMpProductId] = useState("");
  const [mpUnlimited, setMpUnlimited] = useState(true);
  const [mpUntil, setMpUntil] = useState("");
  const [mpNote, setMpNote] = useState("");

  async function load() {
    if (!id) return;
    const d = await apiJson<AdminUserDetail>("GET", `/api/admin/users/${id}`);
    setData(d);
    setName(d.user.name || "");
    setEmail(d.user.email);
    setMpProductId(d.manualPlan?.productId || "");
    setMpUnlimited(d.manualPlan?.unlimited ?? true);
    setMpUntil(d.manualPlan?.until ? d.manualPlan.until.slice(0, 10) : "");
    setMpNote(d.manualPlan?.note || "");
    setDirty(false);
  }

  useEffect(() => {
    if (!id || !user?.isAdmin) return;
    load().catch((e) => setError((e as Error).message));
    apiJson<{ packages: { id: string; name: string }[] }>(
      "GET",
      "/api/admin/subscription/packages",
    )
      .then((d) => setPackages(d.packages.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.isAdmin]);

  if (!user?.isAdmin) {
    return (
      <div className="p-6 bg-[var(--color-nogo-bg)] border border-[var(--color-nogo)] text-[var(--color-nogo)] rounded-md">
        Nur Admins.
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6 bg-[var(--color-nogo-bg)] border border-[var(--color-nogo)] text-[var(--color-nogo)] rounded-md">
        {error}
      </div>
    );
  }
  if (!data) return <div className="text-[var(--color-app-text-secondary)]">Lädt …</div>;

  const isSelf = data.user.id === user.id;

  async function save() {
    if (!id) return;
    setBusy(true);
    setActionError(null);
    setSaveMessage(null);
    try {
      await apiJson("PATCH", `/api/admin/users/${id}`, { name, email });
      await load();
      setSaveMessage("Gespeichert");
      setTimeout(() => setSaveMessage(null), 2000);
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleRole() {
    if (!data || !id) return;
    const next = !data.user.isAdmin;
    const verb = next ? "zum Admin machen" : "die Admin-Rolle entziehen";
    if (!confirm(`${data.user.email} ${verb}?`)) return;
    setBusy(true);
    setActionError(null);
    try {
      await apiJson("PATCH", `/api/admin/users/${id}/role`, { isAdmin: next });
      await load();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword() {
    if (!data || !id) return;
    if (!confirm(`Passwort für ${data.user.email} zurücksetzen?`)) return;
    setBusy(true);
    setActionError(null);
    try {
      const res = await apiJson<{ newPassword: string }>(
        "POST",
        `/api/admin/users/${id}/reset-password`,
      );
      setPwModal({ email: data.user.email, password: res.newPassword });
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function deleteUser() {
    if (!data || !id) return;
    if (
      !confirm(
        `Nutzer ${data.user.email} und ALLE zugehörigen Daten endgültig löschen?\n\nDieser Vorgang kann nicht rückgängig gemacht werden.`,
      )
    )
      return;
    setBusy(true);
    setActionError(null);
    try {
      await apiJson("DELETE", `/api/admin/users/${id}`);
      navigate("/admin");
    } catch (e) {
      setActionError((e as Error).message);
      setBusy(false);
    }
  }

  async function grantManualPlan() {
    if (!id) return;
    if (!mpProductId) {
      setActionError("Bitte ein Paket auswählen.");
      return;
    }
    if (!mpUnlimited && !mpUntil) {
      setActionError("Bitte ein Ablaufdatum wählen oder „unbegrenzt“ aktivieren.");
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      await apiJson("POST", `/api/admin/users/${id}/manual-plan`, {
        productId: mpProductId,
        unlimited: mpUnlimited,
        until: mpUnlimited ? null : mpUntil,
        note: mpNote.trim() || null,
      });
      await load();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function removeManualPlan() {
    if (!id || !data) return;
    if (!confirm("Manuelles Abo / Freischaltung für diesen Nutzer entfernen?")) return;
    setBusy(true);
    setActionError(null);
    try {
      await apiJson("DELETE", `/api/admin/users/${id}/manual-plan`);
      await load();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const sub = data.subscription;
  const hasManual = !!data.manualPlan?.productId;
  const sourceLabel: Record<string, string> = {
    admin: "Admin (global freigeschaltet)",
    manual: "Manuelle Freischaltung",
    stripe: "Stripe-Abo",
    trial: "Testphase",
    none: "Kein Abo",
  };
  function euro(cents: number | null): string {
    if (cents == null) return "—";
    return (cents / 100).toLocaleString("de-DE", {
      style: "currency",
      currency: "EUR",
    });
  }
  function intervalLabel(iv: string | null): string {
    if (iv === "month") return "monatlich";
    if (iv === "year") return "jährlich";
    return iv ?? "einmalig";
  }

  return (
    <div className="space-y-4">
      <Link to="/admin" className="text-sm text-brand-600 hover:underline">
        ← Zurück zur Nutzerliste
      </Link>

      <header className="bg-white border border-[var(--color-app-border)] rounded-lg p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-xl font-semibold text-[var(--color-app-text)]">
            {data.user.name || "(ohne Name)"}
          </h1>
          {data.user.isAdmin && (
            <span className="px-1.5 py-0.5 rounded bg-[var(--color-caution-bg)] text-[var(--color-caution)] text-xs font-semibold uppercase">
              Admin
            </span>
          )}
          {isSelf && (
            <span className="px-1.5 py-0.5 rounded bg-[var(--color-brand-50)] text-[var(--color-brand-600)] text-xs font-semibold uppercase">
              Du
            </span>
          )}
        </div>
        <div className="text-xs text-[var(--color-app-text-secondary)]">
          ID: {data.user.id} · Provider: {data.user.authProvider} · Registriert:{" "}
          {new Date(data.user.createdAt).toLocaleString("de-DE")}
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <label className="text-sm">
            <span className="block text-[var(--color-app-text-secondary)] mb-1">Name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setDirty(true);
              }}
              className="w-full px-3 py-2 border border-[var(--color-app-border)] rounded"
            />
          </label>
          <label className="text-sm">
            <span className="block text-[var(--color-app-text-secondary)] mb-1">E-Mail</span>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setDirty(true);
              }}
              className="w-full px-3 py-2 border border-[var(--color-app-border)] rounded font-mono"
            />
          </label>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <button
            disabled={!dirty || busy}
            onClick={save}
            className="px-3 py-2 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Speichern
          </button>
          {saveMessage && <span className="text-xs text-[var(--color-go)]">{saveMessage}</span>}

          <span className="flex-1" />

          <button
            disabled={busy || (isSelf && data.user.isAdmin)}
            onClick={toggleRole}
            title={
              isSelf && data.user.isAdmin
                ? "Du kannst dir selbst die Admin-Rolle nicht entziehen"
                : undefined
            }
            className="px-3 py-2 text-sm bg-[var(--color-caution-bg)] hover:bg-[var(--color-caution-bg)] text-[var(--color-caution)] rounded disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {data.user.isAdmin ? "Admin-Rolle entziehen" : "Zum Admin machen"}
          </button>
          <button
            disabled={busy}
            onClick={resetPassword}
            className="px-3 py-2 text-sm bg-[var(--color-brand-50)] hover:bg-[var(--color-brand-50)] text-[var(--color-brand-600)] rounded disabled:opacity-40"
          >
            Passwort zurücksetzen
          </button>
          <button
            disabled={busy || isSelf}
            onClick={deleteUser}
            title={isSelf ? "Du kannst dich nicht selbst löschen" : undefined}
            className="px-3 py-2 text-sm bg-[var(--color-nogo-bg)] hover:bg-[var(--color-nogo-bg)] text-[var(--color-nogo)] rounded disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Nutzer löschen
          </button>
        </div>

        {actionError && (
          <div className="text-sm text-[var(--color-nogo)] bg-[var(--color-nogo-bg)] border border-[var(--color-nogo)] rounded px-3 py-2">
            {actionError}
          </div>
        )}
      </header>

      <section
        className="bg-white border border-[var(--color-app-border)] rounded-lg p-4 space-y-4"
        data-testid="section-subscription"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-[var(--color-app-text)]">Abo-Status</h2>
          {sub?.active ? (
            <span className="px-2 py-0.5 rounded bg-[var(--color-go-bg)] text-[var(--color-go)] text-xs font-semibold uppercase">
              Aktiv
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded bg-[var(--color-app-bg-tertiary)] text-[var(--color-app-text-secondary)] text-xs font-semibold uppercase">
              Inaktiv
            </span>
          )}
        </div>

        <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2 text-sm">
          <div className="flex justify-between sm:block">
            <dt className="text-[var(--color-app-text-secondary)]">Quelle</dt>
            <dd className="text-[var(--color-app-text)] sm:mt-0.5" data-testid="text-sub-source">
              {sourceLabel[sub?.source ?? "none"] ?? (sub?.source ?? "—")}
            </dd>
          </div>
          <div className="flex justify-between sm:block">
            <dt className="text-[var(--color-app-text-secondary)]">Paket</dt>
            <dd className="text-[var(--color-app-text)] sm:mt-0.5">
              {sub?.plan?.productName ?? "—"}
            </dd>
          </div>
          <div className="flex justify-between sm:block">
            <dt className="text-[var(--color-app-text-secondary)]">Preis</dt>
            <dd className="text-[var(--color-app-text)] sm:mt-0.5">
              {sub?.plan?.amount != null
                ? `${euro(sub.plan.amount)} ${intervalLabel(sub.plan.interval)}`
                : "—"}
            </dd>
          </div>
          <div className="flex justify-between sm:block">
            <dt className="text-[var(--color-app-text-secondary)]">Stripe-Status</dt>
            <dd className="text-[var(--color-app-text)] sm:mt-0.5">{sub?.status ?? "—"}</dd>
          </div>
          <div className="flex justify-between sm:block">
            <dt className="text-[var(--color-app-text-secondary)]">Läuft bis</dt>
            <dd className="text-[var(--color-app-text)] sm:mt-0.5">
              {sub?.currentPeriodEnd
                ? new Date(sub.currentPeriodEnd).toLocaleDateString("de-DE")
                : "—"}
            </dd>
          </div>
          <div className="flex justify-between sm:block">
            <dt className="text-[var(--color-app-text-secondary)]">Stripe-Kunde</dt>
            <dd className="text-[var(--color-app-text)] sm:mt-0.5 font-mono text-xs">
              {data.stripeCustomerId ?? "—"}
            </dd>
          </div>
        </dl>

        <div className="border-t border-[var(--color-app-border)] pt-4 space-y-3">
          <h3 className="text-sm font-semibold text-[var(--color-app-text)]">
            Manuelle Freischaltung
          </h3>
          {hasManual ? (
            <div className="text-sm bg-[var(--color-go-bg)] border border-[var(--color-go)] rounded px-3 py-2 space-y-1">
              <div>
                <span className="text-[var(--color-app-text-secondary)]">Paket: </span>
                {packages.find((p) => p.id === data.manualPlan.productId)?.name ??
                  data.manualPlan.productId}
              </div>
              <div>
                <span className="text-[var(--color-app-text-secondary)]">Gültigkeit: </span>
                {data.manualPlan.unlimited
                  ? "unbegrenzt"
                  : data.manualPlan.until
                    ? new Date(data.manualPlan.until).toLocaleDateString("de-DE")
                    : "—"}
              </div>
              {data.manualPlan.note ? (
                <div>
                  <span className="text-[var(--color-app-text-secondary)]">Notiz: </span>
                  {data.manualPlan.note}
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-[var(--color-app-text-tertiary)] italic">
              Keine manuelle Freischaltung aktiv.
            </p>
          )}

          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm">
              <span className="block text-[var(--color-app-text-secondary)] mb-1">Paket</span>
              <select
                className="w-full px-3 py-2 border border-[var(--color-app-border)] rounded text-sm"
                value={mpProductId}
                onChange={(e) => setMpProductId(e.target.value)}
                data-testid="select-manual-package"
              >
                <option value="">– Paket wählen –</option>
                {packages.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="block text-[var(--color-app-text-secondary)] mb-1">Gültig bis</span>
              <input
                type="date"
                className="w-full px-3 py-2 border border-[var(--color-app-border)] rounded text-sm disabled:opacity-40"
                value={mpUntil}
                onChange={(e) => setMpUntil(e.target.value)}
                disabled={mpUnlimited}
                data-testid="input-manual-until"
              />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm text-[var(--color-app-text)]">
            <input
              type="checkbox"
              checked={mpUnlimited}
              onChange={(e) => setMpUnlimited(e.target.checked)}
              data-testid="checkbox-manual-unlimited"
            />
            Unbegrenzt gültig (kein Ablaufdatum)
          </label>
          <label className="text-sm block">
            <span className="block text-[var(--color-app-text-secondary)] mb-1">Notiz (optional)</span>
            <input
              type="text"
              className="w-full px-3 py-2 border border-[var(--color-app-border)] rounded text-sm"
              value={mpNote}
              onChange={(e) => setMpNote(e.target.value)}
              placeholder="z.B. Partner-Account, Support-Kulanz"
              data-testid="input-manual-note"
            />
          </label>
          <div className="flex gap-2">
            <button
              className="px-3 py-2 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded disabled:opacity-40 disabled:cursor-not-allowed"
              disabled={busy}
              onClick={grantManualPlan}
              data-testid="button-grant-manual"
            >
              {hasManual ? "Freischaltung aktualisieren" : "Kostenlos freischalten"}
            </button>
            {hasManual ? (
              <button
                className="px-3 py-2 text-sm bg-[var(--color-nogo-bg)] hover:bg-[var(--color-nogo-bg)] text-[var(--color-nogo)] rounded disabled:opacity-40"
                disabled={busy}
                onClick={removeManualPlan}
                data-testid="button-remove-manual"
              >
                Entfernen
              </button>
            ) : null}
          </div>
        </div>
      </section>

      <div className="grid gap-4 md:grid-cols-2">
        <Section title="Profil" count={data.profile ? 1 : 0}>
          {data.profile ? (
            <pre className="bg-[var(--color-app-bg-secondary)] border border-[var(--color-app-border)] rounded p-2 text-xs overflow-x-auto">
              {JSON.stringify(data.profile, null, 2)}
            </pre>
          ) : (
            <div className="text-[var(--color-app-text-tertiary)] text-sm italic">Kein Profil.</div>
          )}
        </Section>
        <Section title="Einstellungen" count={data.settings ? 1 : 0}>
          {data.settings ? (
            <pre className="bg-[var(--color-app-bg-secondary)] border border-[var(--color-app-border)] rounded p-2 text-xs overflow-x-auto">
              {JSON.stringify(data.settings, null, 2)}
            </pre>
          ) : (
            <div className="text-[var(--color-app-text-tertiary)] text-sm italic">Keine Einstellungen.</div>
          )}
        </Section>
        <Section title="Drohnen" count={data.drones.length}>
          <JsonList items={data.drones} />
        </Section>
        <Section title="Drohnen-Historie" count={data.droneHistory.length}>
          <JsonList items={data.droneHistory} />
        </Section>
        <Section title="Flug-Sessions (PreCheck)" count={data.sessions.length}>
          <JsonList items={data.sessions} />
        </Section>
        <Section title="Logbuch" count={data.logbook.length}>
          <JsonList items={data.logbook} />
        </Section>
        <Section title="Glossar" count={data.glossary.length}>
          <JsonList items={data.glossary} />
        </Section>
        <Section title="Ressourcen / Links" count={data.resources.length}>
          <JsonList items={data.resources} />
        </Section>
      </div>

      {pwModal && (
        <PasswordModal
          email={pwModal.email}
          password={pwModal.password}
          onClose={() => setPwModal(null)}
        />
      )}
    </div>
  );
}
